import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import Button from "../../../components/button";
import { openVideoList } from "../../../helpers/service";
import VideoSettingsComponent from "../../../components/Events/VideoSettings/VideoSettings";

// Style
import {
  BottomFields,
  EventsMatchesWrap,
  FieldCount,
  FootballField,
  FootballFieldCount,
  LeftTopField,
  ListOfMatches,
  MiddleTopField,
  RightTopField,
  TopFields,
} from "../../../style/EventsMatchesStyle";

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  DirectionStatus,
  EventCard,
  EventCardWrapper,
  EventContent,
  FootballFielOptions,
  ParametersTabWrapper,
} from "../shared/ui/EventSharedStyles";

import {
  TabContent,
  TabListWrapper,
  TabsWrapper,
  TabTitle,
  TopList,
} from "../../../style/Favorites";
import { TimelineChange } from "../../../components/Events/TimelineChange";
import { mirrorCoordinates } from "../../../helpers";
import { useLexicon } from "../../../hooks/useLexicon";
import { ChoosenSelectionsPopover } from "../../../components/Events/ChooseSeasonSelector/ChoosenSelectionsPopover";
import { useSelector } from "react-redux";
import {
  getMarkerEventColor,
} from "../../../helpers/eventMarkerColors";
import OwnOpponentSwitcher from "../shared/ui/OwnOpponentSwitcher";
import WatchEventsButton from "../shared/ui/WatchEventsButton";
import useActionSelection from "../shared/hooks/useActionSelection";
import useTimelineFilter from "../shared/hooks/useTimelineFilter";
import useTeamDualSideEventsData from "../shared/hooks/useTeamDualSideEventsData";
import useEventMarkerHover from "../shared/hooks/useEventMarkerHover";
import {
  PitchTrackerFrame,
  PitchTrackerSvg,
} from "../shared/ui/EventPitchTrackerStyles";
import EventHalfPitchBaseLayer from "../shared/ui/EventHalfPitchBaseLayer";
import EventHoverTracker from "../shared/ui/EventHoverTracker";
import PitchLoadingOverlay from "../shared/ui/PitchLoadingOverlay";
import SelectedActionsPopoverContent from "../shared/ui/SelectedActionsPopoverContent";
import EventHalfPitchStaticPreview from "../shared/ui/EventHalfPitchStaticPreview";
import {
  convertHalfPitchCoordinates,
} from "../shared/utils/pitchMath";

export { Dropdown, DropdownButton, DropdownContent, DropdownItem };

const _isLightTheme = localStorage.getItem('theme') === 'light';

const CrossesTeam = ({ players, paramsShotList, user, selectedMatchIds = [] }) => {
  const l = useLexicon();
  const { id } = useParams();
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [showDirection, setShowDirection] = useState(false);
  const [selectedZones, setSelectedZones] = useState([]);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [typeShots, setTypeShots] = useState('own'); // own | opponent
  const dataForTimeLineChange = useRef(null);
  const userState = useSelector((state) => state.user.userProfile);
  const {
    checkedIds,
    checkedAction,
    groupedSelectedActions,
    handleCheckboxChange,
    resetActionSelection,
  } = useActionSelection();

  const {
    response,
    responseOpponents,
    ownMarkerColorMap,
    opponentMarkerColorMap,
    allOwnMarkers,
    allOpponentsMarkers,
    loadingMarkers,
    timeLineRange,
    resetData,
  } = useTeamDualSideEventsData({
    id,
    matchesList: selectedMatchIds,
    checkedIds,
    checkedAction,
    mergeOffset: userState?.merge_offset,
    keepMaxTimeline: true,
  });

  const { getDebouncedTimelineHandler } = useTimelineFilter({ debounceMs: 500 });

  const {
    hoveredEvent,
    hoveredEventColor,
    hoverCursor,
    handleMarkerHoverStart,
    handleMarkerHoverMove,
    clearMarkerHover,
    resetHover,
  } = useEventMarkerHover();

  const currentDisplayedReponseData = useMemo(() => {
    if (typeShots === 'own') {
      return response
    } else {
      return responseOpponents;
    }
  }, [response, responseOpponents, typeShots]);

  const currentDisplayedMarkerColorMap = useMemo(() => {
    return typeShots === "own" ? ownMarkerColorMap : opponentMarkerColorMap;
  }, [typeShots, ownMarkerColorMap, opponentMarkerColorMap]);

  const currentDisplayedAllMarkers = useMemo(() => {
    return typeShots === "own" ? allOwnMarkers : allOpponentsMarkers;
  }, [allOpponentsMarkers, allOwnMarkers, typeShots]);

  const timelineScopedMergeMarkers = useMemo(() => {
    const sourceMergeMarkers = currentDisplayedAllMarkers || [];
    if (sourceMergeMarkers.length === 0) {
      return [];
    }

    const displayedEventIds = new Set(
      (displayedEvents || []).map((item) => String(item?.id))
    );
    if (displayedEventIds.size === 0) {
      return [];
    }

    return sourceMergeMarkers.filter((markerId) =>
      displayedEventIds.has(String(markerId))
    );
  }, [currentDisplayedAllMarkers, displayedEvents]);

  useEffect(() => {
    dataForTimeLineChange.current = currentDisplayedReponseData;
    setDisplayedEvents(currentDisplayedReponseData?.events ?? []);
  }, [currentDisplayedReponseData]);

  const debouncedChangeTimeLineRange = useMemo(
    () =>
      getDebouncedTimelineHandler({
        getEvents: () => dataForTimeLineChange.current?.events || [],
        setDisplayedEvents,
      }),
    [getDebouncedTimelineHandler]
  );

  useEffect(() => {
    return () => {
      debouncedChangeTimeLineRange?.cancel?.();
    };
  }, [debouncedChangeTimeLineRange]);

  const fieldWidth = 649;
  const fieldHeight = 482;
  const fieldPadding = 0;

  const convertCoordinatesWithPadding = useCallback(
    (coord_x, coord_y) =>
      convertHalfPitchCoordinates({
        coordX: coord_x,
        coordY: coord_y,
        fieldWidth,
        fieldHeight,
      }),
    [fieldHeight, fieldWidth]
  );

  const counts = useMemo(() => {
    const nextCounts = {
      leftTop: 0,
      middleTop: 0,
      rightTop: 0,
      bottom: 0,
    };

    displayedEvents?.forEach((event) => {
      const { coord_x, coord_y } = mirrorCoordinates(event);
      if (coord_x < 105 / 4) {
        if (coord_y >= 0 && coord_y <= 28.66) {
          nextCounts.leftTop += 1;
        } else if (coord_y > 28.66 && coord_y <= 57.33) {
          nextCounts.middleTop += 1;
        } else if (coord_y > 57.33 && coord_y <= 86) {
          nextCounts.rightTop += 1;
        }
      } else {
        nextCounts.bottom += 1;
      }
    });

    return nextCounts;
  }, [displayedEvents]);

  const resetAllSettings = () => {
    setDisplayedEvents([]);
    setTypeShots("own");
    resetHover();
    resetActionSelection();
    resetData();
  };

  const PopoverContent = useMemo(() => {
    if (!groupedSelectedActions) {
      return null;
    }

    return (
      <SelectedActionsPopoverContent
        groupedSelectedActions={groupedSelectedActions}
        l={l}
      />
    );
  }, [groupedSelectedActions, l])

  const handleZoneSelection = (zone) => {
    setSelectedZones(prev => {
      if (prev.includes(zone)) {
        return prev.filter(z => z !== zone);
      }
      return [...prev, zone];
    });
  };

  return (
    <>
      <EventsMatchesWrap>
        <ListOfMatches>
          <EventCardWrapper>
            <OwnOpponentSwitcher
              value={typeShots}
              onChange={setTypeShots}
              ownLabel={l(1078)}
              opponentLabel={"Opponent Crosses"}
            />
          </EventCardWrapper>

          <EventCardWrapper>
            <h3>
              {l(996)}
              {groupedSelectedActions && (
                <ChoosenSelectionsPopover content={PopoverContent}>Show selections</ChoosenSelectionsPopover>
              )}
            </h3>
            <EventCard style={{ maxHeight: "400px", overflow: "scroll" }}>
              <EventContent>
                <TabsWrapper>
                  <TopList
                    $needBackGroundColor={true}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <TabListWrapper
                      style={{
                        overflow: "visible",
                      }}
                    >
                      <TabTitle>
                        {l(587)}
                        {(groupedSelectedActions?.[587]?.length || 0) > 0 && <span>{groupedSelectedActions?.[587]?.length || 0}</span>}
                      </TabTitle>
                    </TabListWrapper>
                  </TopList>
                  <TabContent>
                    <ParametersTabWrapper>
                      {paramsShotList?.filter(
                        (item) =>
                          item.name.includes("Cross") &&
                          !item.name.includes("%")
                      )?.map((item, index) => (
                        <div className="checkboxField" key={index}>
                          <label htmlFor={item.id}>
                            <input
                              id={item.id}
                              type="checkbox"
                              checked={checkedIds.includes(item.id)}
                              onChange={() => handleCheckboxChange(item, { title: 'Crosses', lexic_id: 587 })}
                            />
                            {l(item.lexic) || item.name}
                          </label>
                        </div>
                      ))}
                    </ParametersTabWrapper>
                  </TabContent>
                </TabsWrapper>
              </EventContent>
            </EventCard>
          </EventCardWrapper>
        </ListOfMatches>  

        <FootballField>
            <FootballFielOptions>
              {/* <VideoSettingsWrapper></VideoSettingsWrapper> */}
              <Button
                onClick={() => resetAllSettings()}
                title={l(457)}
                size={"normal"}
              />
              <WatchEventsButton
                count={displayedEvents?.length}
                onClick={() =>
                  openVideoList(
                    displayedEvents?.map((item) => item.id)?.join(","),
                    `${typeShots === "own" ? l(9) : `${l(122)} ${l(9)}`} - ${players?.team?.name}`,
                    false,
                    timelineScopedMergeMarkers
                  )
                }
                onContextMenu={() =>
                  openVideoList(
                    displayedEvents?.map((item) => item.id)?.join(","),
                    `${typeShots === "own" ? l(9) : `${l(122)} ${l(9)}`} - ${players?.team?.name}`,
                    false,
                    timelineScopedMergeMarkers
                  )
                }
              />
            </FootballFielOptions>
            <VideoSettingsComponent
                showDirection={showDirection}
                setShowDirection={setShowDirection}
                selectedZones={selectedZones}
                handleZoneSelection={handleZoneSelection}
                zoneOpen={zoneOpen}
                setZoneOpen={setZoneOpen}
                user={user}
                showZones={false}
              />
              <div style={{height: 10}} />

            <DirectionStatus>
              <label>
                <input
                  type="checkbox"
                  id="specifyColor"
                  checked={showDirection}
                  onChange={() => setShowDirection(!showDirection)}
                />
                {l(421)}
              </label>
            </DirectionStatus>

            <PitchLoadingOverlay loading={loadingMarkers}>
              <PitchTrackerFrame>
                <PitchTrackerSvg
                  width={fieldWidth}
                  height={fieldHeight}
                  viewBox={`0 0 ${fieldWidth} ${fieldHeight}`}
                  preserveAspectRatio="xMidYMid meet"
                  $drawing={false}
                  $grassStripeDirection="horizontal"
                >
                  <EventHalfPitchBaseLayer
                    fieldWidth={fieldWidth}
                    fieldHeight={fieldHeight}
                    fieldPadding={fieldPadding}
                  />
                  {displayedEvents?.map((event) => {
                    const {
                      coord_x,
                      coord_y,
                      coord_x_destination,
                      coord_y_destination,
                    } = mirrorCoordinates(event);
                    const position = convertCoordinatesWithPadding(coord_x, coord_y);
                    const eventColor = getMarkerEventColor(
                      event.id,
                      currentDisplayedMarkerColorMap
                    );
                    const arrowsEnd = convertCoordinatesWithPadding(
                      coord_x_destination,
                      coord_y_destination
                    );
                    const isHovered = hoveredEvent?.id === event.id;

                    return (
                      <React.Fragment key={event.id}>
                        {showDirection && arrowsEnd?.x > 0 && arrowsEnd.y >= 0 && (
                          <>
                            <defs>
                              <marker
                                id={`arrow-${event.id}`}
                                markerWidth="5"
                                markerHeight="5"
                                refX="0"
                                refY="2"
                                orient="auto"
                              >
                                <path d="M0,0 L0,3 L4,1.5 z" fill="#000" />
                              </marker>
                            </defs>
                            <line
                              x1={position.x}
                              y1={position.y}
                              x2={arrowsEnd.x}
                              y2={arrowsEnd.y}
                              stroke={_isLightTheme ? "#2363F7" : "#BFE82D"}
                              strokeWidth="2"
                              strokeDasharray="0"
                            />
                          </>
                        )}
                        <g
                          className={`event-dot-group ${isHovered ? "is-hovered" : ""}`}
                          onClick={() =>
                            openVideoList(
                              event.id,
                              `${event.action} - ${event?.creator?.display_name}`
                            )
                          }
                          onContextMenu={() =>
                            openVideoList(
                              event.id,
                              `${event.action} - ${event?.creator?.display_name}`
                            )
                          }
                          onMouseEnter={(nativeEvent) =>
                            handleMarkerHoverStart(event, eventColor, nativeEvent)
                          }
                          onMouseMove={handleMarkerHoverMove}
                          onMouseLeave={clearMarkerHover}
                        >
                          <circle
                            className="event-dot-hover-ring"
                            cx={position.x}
                            cy={position.y}
                            r="9.6"
                            stroke={eventColor}
                          />
                          <circle
                            className="event-dot-core"
                            cx={position.x}
                            cy={position.y}
                            r="5"
                            fill={eventColor}
                          />
                        </g>
                      </React.Fragment>
                    );
                  })}
                </PitchTrackerSvg>
              </PitchTrackerFrame>
            </PitchLoadingOverlay>

            <EventHoverTracker
              event={hoveredEvent}
              visible={!!hoveredEvent}
              badgeColor={hoveredEventColor}
              cursorX={hoverCursor.x}
              cursorY={hoverCursor.y}
              emptyText={l(464)}
            />

            <TimelineChange
              onChange={debouncedChangeTimeLineRange}
              min={timeLineRange?.min}
              max={timeLineRange?.max}
              styleContainer={{ marginTop: 35, marginBottom: 20 }}
            />

            <FootballFieldCount>
              <EventHalfPitchStaticPreview
                fieldWidth={fieldWidth}
                fieldHeight={fieldHeight}
                fieldPadding={fieldPadding}
              />

              <FieldCount>
                <TopFields>
                  <LeftTopField>
                    {displayedEvents?.length > 0
                      ? `${Math.round(
                        (counts.leftTop / displayedEvents?.length) * 100
                      )} %`
                      : "0"}
                  </LeftTopField>
                  <MiddleTopField>
                    {displayedEvents?.length > 0
                      ? `${Math.round(
                        (counts.middleTop / displayedEvents?.length) * 100
                      )} %`
                      : "0"}
                  </MiddleTopField>
                  <RightTopField>
                    {displayedEvents?.length > 0
                      ? `${Math.round(
                        (counts.rightTop / displayedEvents?.length) * 100
                      )} %`
                      : "0"}
                  </RightTopField>
                </TopFields>
                <BottomFields>
                  {displayedEvents?.length > 0
                    ? `${Math.round(
                      (counts.bottom / displayedEvents?.length) * 100
                    )} %`
                    : "0"}
                </BottomFields>
              </FieldCount>
            </FootballFieldCount>
        </FootballField>
      </EventsMatchesWrap>
    </>
  );
};

export default CrossesTeam;
